import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useState, useMemo, useCallback } from "react";
import { useWidgetConfig, useThemeFonts, WidgetEditUnsuppressProvider, ShareConversationDialogWidget } from "@brandclub/bc-widgets";
import { ResponsiveDialog } from "../../Components/Dialog/ResponsiveDialog.js";
function resolveFontFamilyCss(fontKey, themeFonts) {
  if (!fontKey || fontKey === "inherit") return void 0;
  const fontData = themeFonts[fontKey];
  if (!fontData) return void 0;
  return fontData.format === "system" ? fontData.url : `"${fontData.name}", sans-serif`;
}
const Spinner = () => (
  // Rotate a block wrapper (span) instead of the <svg> directly. On iOS Safari
  // rotating an <svg> element can use an unexpected transform-origin/
  // transform-box, making the spinner orbit through its siblings ("Copy link"
  // text). A span with explicit width/height + flex-shrink:0 spins in place.
  /* @__PURE__ */ jsx(
    "span",
    {
      "aria-hidden": "true",
      style: {
        display: "inline-block",
        width: 16,
        height: 16,
        flexShrink: 0,
        animation: "share-dialog-spin 0.8s linear infinite",
        transformOrigin: "50% 50%"
      },
      children: /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", style: { display: "block" }, children: [
        /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6.5", stroke: "currentColor", strokeWidth: "2", opacity: "0.3" }),
        /* @__PURE__ */ jsx("path", { d: "M14.5 8a6.5 6.5 0 00-6.5-6.5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })
      ] })
    }
  )
);
const ShareConversationDialog = ({
  open,
  onClose,
  onCopyLink,
  brandLogoUrl
}) => {
  const [shareState, setShareState] = useState("idle");
  const homeInputConfig = useWidgetConfig("concierge-chat-input", {});
  const { themeFonts } = useThemeFonts();
  const fontFamilyCss = useMemo(
    () => resolveFontFamilyCss(homeInputConfig.fontFamily, themeFonts),
    [homeInputConfig.fontFamily, themeFonts]
  );
  const handleCopyLink = useCallback(() => {
    if (shareState === "loading") return;
    setShareState("loading");
    const urlPromise = onCopyLink();
    const copyWithClipboardItem = async () => {
      try {
        const item = new ClipboardItem({
          "text/plain": urlPromise.then((url) => new Blob([url], { type: "text/plain" }))
        });
        await navigator.clipboard.write([item]);
        setShareState("copied");
      } catch {
        try {
          const url = await urlPromise;
          await navigator.clipboard.writeText(url);
          setShareState("copied");
        } catch {
          setShareState("idle");
        }
      }
    };
    copyWithClipboardItem();
  }, [shareState, onCopyLink]);
  const handleClose = useCallback(() => {
    setShareState("idle");
    onClose();
  }, [onClose]);
  const buttonText = shareState === "copied" ? "Link copied!" : "Copy link";
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("style", { children: `
        @keyframes share-dialog-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @media(max-width:899.95px){.share-dialog-content{border-bottom-left-radius:0!important;border-bottom-right-radius:0!important}}
      ` }),
    /* @__PURE__ */ jsx(
      ResponsiveDialog,
      {
        open,
        onClose: handleClose,
        disableScrollLock: true,
        sx: (theme) => ({
          zIndex: 1e4,
          "& div.MuiDialog-paper": {
            margin: "0 20px",
            width: "100%",
            maxWidth: 400,
            overflow: "visible",
            backgroundColor: "transparent",
            boxShadow: "none",
            paddingTop: 0,
            [theme.breakpoints.down("md")]: {
              maxWidth: "100%",
              width: "100%",
              margin: 0,
              padding: 0,
              paddingTop: "12px",
              boxSizing: "border-box"
            }
          }
        }),
        children: /* @__PURE__ */ jsx(WidgetEditUnsuppressProvider, { children: /* @__PURE__ */ jsx(
          ShareConversationDialogWidget,
          {
            widgetId: "concierge-share-conversation-dialog",
            displayName: "Share Conversation Dialog",
            children: (styles, resolveColor, resolvedLogoUrl) => {
              const logoSrc = resolvedLogoUrl || brandLogoUrl;
              return /* @__PURE__ */ jsx(
                "div",
                {
                  className: "share-dialog-content",
                  style: {
                    backgroundColor: resolveColor(styles.dialogBgColor),
                    borderRadius: styles.dialogBorderRadius,
                    boxShadow: "8px 8px 14px 0px rgba(0,0,0,0.03)",
                    border: "1px solid rgba(13,13,13,0.05)",
                    overflow: "hidden",
                    ...fontFamilyCss ? { fontFamily: fontFamilyCss } : {}
                  },
                  children: /* @__PURE__ */ jsxs(
                    "div",
                    {
                      style: {
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                        padding: styles.dialogPadding,
                        boxSizing: "border-box"
                      },
                      children: [
                        /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }, children: [
                          /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 24 }, children: [
                            logoSrc && /* @__PURE__ */ jsx(
                              "div",
                              {
                                style: {
                                  width: 64,
                                  height: 64,
                                  borderRadius: 0,
                                  backgroundColor: resolveColor(styles.logoContainerBgColor),
                                  overflow: "hidden",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0
                                },
                                children: /* @__PURE__ */ jsx("img", { src: logoSrc, alt: "", style: { width: "100%", height: "100%", objectFit: "contain" } })
                              }
                            ),
                            /* @__PURE__ */ jsx(
                              "p",
                              {
                                style: {
                                  fontSize: styles.titleFontSize,
                                  fontWeight: 600,
                                  lineHeight: "25px",
                                  textAlign: "center",
                                  color: resolveColor(styles.titleColor),
                                  margin: 0
                                },
                                children: "Share conversation"
                              }
                            )
                          ] }),
                          /* @__PURE__ */ jsx(
                            "p",
                            {
                              style: {
                                fontSize: styles.descriptionFontSize,
                                fontWeight: 500,
                                lineHeight: "1.5",
                                textAlign: "center",
                                color: resolveColor(styles.descriptionColor),
                                margin: 0,
                                maxWidth: 320
                              },
                              children: "Copy the link below to share this conversation with others."
                            }
                          )
                        ] }),
                        /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }, children: [
                          /* @__PURE__ */ jsxs(
                            "button",
                            {
                              type: "button",
                              onClick: handleCopyLink,
                              disabled: shareState === "loading",
                              style: {
                                width: "100%",
                                backgroundColor: resolveColor(styles.primaryButtonBgColor),
                                color: resolveColor(styles.primaryButtonTextColor),
                                border: "none",
                                borderRadius: styles.primaryButtonBorderRadius,
                                padding: "13px 16px",
                                fontSize: 14,
                                fontWeight: 600,
                                lineHeight: "20px",
                                cursor: shareState === "loading" ? "wait" : "pointer",
                                opacity: shareState === "loading" ? 0.7 : 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8,
                                transition: "opacity 0.15s ease",
                                fontFamily: "inherit"
                              },
                              children: [
                                /* @__PURE__ */ jsx(
                                  "span",
                                  {
                                    "aria-hidden": "true",
                                    style: {
                                      width: 16,
                                      height: 16,
                                      flexShrink: 0,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      opacity: shareState === "loading" ? 1 : 0,
                                      transition: "opacity 0.15s ease"
                                    },
                                    children: shareState === "loading" && /* @__PURE__ */ jsx(Spinner, {})
                                  }
                                ),
                                /* @__PURE__ */ jsx(
                                  "span",
                                  {
                                    style: { transform: "translateZ(0)" },
                                    children: buttonText
                                  },
                                  buttonText
                                )
                              ]
                            }
                          ),
                          /* @__PURE__ */ jsx(
                            "button",
                            {
                              type: "button",
                              onClick: handleClose,
                              style: {
                                background: "none",
                                border: "none",
                                padding: 0,
                                fontSize: 14,
                                fontWeight: 600,
                                lineHeight: "20px",
                                color: resolveColor(styles.cancelTextColor),
                                cursor: "pointer",
                                fontFamily: "inherit"
                              },
                              children: "Cancel"
                            }
                          )
                        ] })
                      ]
                    }
                  )
                }
              );
            }
          }
        ) })
      }
    )
  ] });
};
export {
  ShareConversationDialog
};
//# sourceMappingURL=ShareConversationDialog.js.map
